export const isKeyInObject = <K extends Array<string>, T extends Record<string, unknown>>(keys: K, obj: T) => {
  return keys.some((key) => obj.hasOwnProperty(key));
};

/**
 * Performs a shallow merge of two objects, only overwriting properties from source
 * that are explicitly defined in target. Works recursively for nested objects.
 * @param source The original object
 * @param target The object with properties to merge into source
 * @returns A new object with the merged properties
 */
export const shallowMergeImmutable = (
  source: Record<string, any>,
  target: Record<string, any>
): Record<string, any> => {
  // Create a new object to avoid mutating the source
  const result = { ...source };

  // Iterate through properties in target
  for (const key in target) {
    if (Object.prototype.hasOwnProperty.call(target, key)) {
      const targetValue = target[key];

      // Skip undefined values (considered as "not specified")
      if (targetValue === undefined) {
        continue;
      }

      // If both source and target have objects at this key, merge them recursively
      if (
        typeof targetValue === 'object' &&
        targetValue !== null &&
        !Array.isArray(targetValue) &&
        typeof source[key] === 'object' &&
        source[key] !== null &&
        !Array.isArray(source[key])
      ) {
        result[key] = shallowMergeImmutable(source[key], targetValue);
      } else {
        // Otherwise, use the target's value
        result[key] = targetValue;
      }
    }
  }

  return result;
};
